import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReportEntity, ReportHeaderFieldDto } from './thunks/types';

export type ReportsListState = {
  reportsList: ReportEntity[];
  headerFields: ReportHeaderFieldDto[];
  isReady?: boolean;
};

type ReportingState = {
  systemImbalance: ReportsListState;
  balancingGroup: ReportsListState;
  deviceProductionPlan: ReportsListState;
  deliveryPointProductionPlan: ReportsListState;
  economicBenefits: {
    economicBenefitChart: ReportsListState;
    cumulativeBenefitChart: ReportsListState;
    economicBenefitTable: ReportsListState;
    cumulativeBenefitTable: ReportsListState;
  };
};

const initialState: ReportingState = {
  systemImbalance: {
    reportsList: [],
    headerFields: []
  },
  balancingGroup: {
    reportsList: [],
    headerFields: []
  },
  deviceProductionPlan: {
    reportsList: [],
    headerFields: []
  },
  deliveryPointProductionPlan: {
    reportsList: [],
    headerFields: []
  },
  economicBenefits: {
    economicBenefitChart: {
      reportsList: [],
      headerFields: []
    },
    cumulativeBenefitChart: {
      reportsList: [],
      headerFields: []
    },
    economicBenefitTable: {
      reportsList: [],
      headerFields: []
    },
    cumulativeBenefitTable: {
      reportsList: [],
      headerFields: []
    }
  }
};

const reportingSlice = createSlice({
  name: 'reporting',
  initialState,
  reducers: {
    clearReportingState: () => {
      return { ...initialState };
    },
    setSystemImbalanceReports: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.systemImbalance = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    },
    setBalancingGroupReports: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.balancingGroup = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    },
    setDeviceProductionPlanReports: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.deviceProductionPlan = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    },
    setDeliveryPointProductionReports: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.deliveryPointProductionPlan = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    },
    setEconomicBenefitReportsForChart: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.economicBenefits.economicBenefitChart = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    },
    setEconomicBenefitReportsForTable: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.economicBenefits.economicBenefitTable = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    },
    setCumulativeBenefitReportsForChart: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.economicBenefits.cumulativeBenefitChart = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    },
    setCumulativeBenefitReportsForTable: (
      state,
      action: PayloadAction<ReportsListState>
    ) => {
      state.economicBenefits.cumulativeBenefitTable = {
        reportsList: action.payload.reportsList,
        headerFields: action.payload.headerFields,
        isReady: action.payload.isReady
      };
    }
  }
});

export const {
  setSystemImbalanceReports,
  setBalancingGroupReports,
  setDeviceProductionPlanReports,
  setDeliveryPointProductionReports,
  setEconomicBenefitReportsForChart,
  setEconomicBenefitReportsForTable,
  setCumulativeBenefitReportsForChart,
  setCumulativeBenefitReportsForTable,
  clearReportingState
} = reportingSlice.actions;

export const reporting = reportingSlice.reducer;
